import { localDate, type LocalDate } from '../dates';
import type { DomainEvent } from '../events';
import type { Mutation } from '../mutations';
import { isOtherIncome } from '../budget/income';
import { spendingMutation, type BuildCtx, type SpendResult } from './types';

export const MAX_AMOUNT_MINOR = 1_000_000_000; // ₱10,000,000.00
export const MAX_NOTE_LENGTH = 200;

const METHOD_RE = /^(cash|account|card:[A-Za-z0-9:._-]+)$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** The user-editable fields; names match the ledgerEntries Drizzle properties. */
export interface ExpenseFields {
  amountMinor: number;
  categoryId: string | null;
  note: string | null;
  date: LocalDate;
  method: string | null; // 'cash' | 'account' | 'card:<payee id>' | null
  /** Budget (v0.2): the fund the expense deducts from. Optional, so forms without Budget leave it alone. */
  fundId?: string | null;
  /** v0.5 Accounts: the account it was paid from (null = none, e.g. a card purchase). */
  accountId?: string | null;
}

export interface ExpenseDraft {
  amountMinor: number;
  categoryId?: string | null;
  note?: string | null;
  date?: LocalDate;
  method?: string | null;
  /** Budget (v0.2): a fund picked on the entry. The budget.deduct rule falls back to the default fund. */
  fundId?: string | null;
  /** v0.5: the "From" account, already resolved by the caller (payFromAccount). */
  accountId?: string | null;
}

export type ExpensePatch = Partial<ExpenseFields>;

export type ExpenseError =
  'amount_required' | 'amount_too_large' | 'note_too_long' | 'bad_date' | 'bad_method';

const ALL_FIELDS: ReadonlyArray<keyof ExpenseFields> = [
  'amountMinor',
  'categoryId',
  'note',
  'date',
  'method',
  'fundId',
  'accountId',
];

export function cleanNote(note: string | null | undefined): string | null {
  const n = (note ?? '').trim().replace(/\s+/g, ' ');
  return n === '' ? null : n;
}

export function validateExpense(d: ExpensePatch): ExpenseError | null {
  if (d.amountMinor !== undefined) {
    if (!Number.isInteger(d.amountMinor) || d.amountMinor <= 0) return 'amount_required';
    if (d.amountMinor > MAX_AMOUNT_MINOR) return 'amount_too_large';
  }
  if (d.note != null && d.note.length > MAX_NOTE_LENGTH) return 'note_too_long';
  if (d.date !== undefined && !DATE_RE.test(d.date)) return 'bad_date';
  if (d.method != null && !METHOD_RE.test(d.method)) return 'bad_method';
  return null;
}

export function buildAddExpense(
  draft: ExpenseDraft,
  ctx: BuildCtx,
): SpendResult<{ entryId: string; mutation: Mutation; event: DomainEvent }, ExpenseError> {
  const fields: ExpenseFields = {
    amountMinor: draft.amountMinor,
    categoryId: draft.categoryId ?? null,
    note: cleanNote(draft.note),
    date: draft.date ?? localDate(ctx.now, ctx.tz),
    method: draft.method ?? null,
    fundId: draft.fundId ?? null,
    // A card purchase charges the card, never an account.
    accountId: draft.method?.startsWith('card:') ? null : (draft.accountId ?? null),
  };
  const error = validateExpense(fields);
  if (error) return { ok: false, error };
  const entryId = ctx.newId();
  const mutation = spendingMutation(ctx, 'ledger_entries', 'insert', entryId, {
    date: fields.date,
    amountMinor: fields.amountMinor,
    currency: 'PHP',
    kind: 'expense',
    categoryId: fields.categoryId,
    method: fields.method,
    // The caller passes the resolved fund (resolveDeductFund: picked, else the default fund while
    // auto-deduct is on, and only while Budget is usable), so the insert carries it (spec §8).
    fundId: fields.fundId ?? null,
    accountId: fields.accountId ?? null,
    sourceModule: 'spending',
    sourceId: null,
    note: fields.note,
    recordedTz: ctx.tz,
  });
  const event: DomainEvent = {
    type: 'expense.created',
    expenseId: entryId,
    amountMinor: fields.amountMinor,
    categoryId: fields.categoryId,
    fundId: fields.fundId ?? null,
    date: fields.date,
    method: fields.method,
    sourceModule: 'spending',
    at: ctx.now,
    mutationId: mutation.id,
  };
  return { ok: true, entryId, mutation, event };
}

/** Writes only fields that differ from `before`. `undo` is the patch that puts them back. */
export function buildEditExpense(
  id: string,
  before: ExpenseFields,
  patch: ExpensePatch,
  ctx: BuildCtx,
): SpendResult<{ mutation: Mutation | null; undo: ExpensePatch }, ExpenseError> {
  const next: ExpensePatch = { ...patch };
  if ('note' in patch) next.note = cleanNote(patch.note);
  // v0.5: a card purchase charges the card, so switching to a card drops the account.
  if (next.method?.startsWith('card:')) next.accountId = null;
  const error = validateExpense(next);
  if (error) return { ok: false, error };

  const values: Record<string, unknown> = {};
  const undo: Record<string, unknown> = {};
  for (const k of ALL_FIELDS) {
    const v = next[k];
    if (v === undefined || v === before[k]) continue;
    values[k] = v;
    undo[k] = before[k];
  }
  if (Object.keys(values).length === 0) return { ok: true, mutation: null, undo: {} };
  return {
    ok: true,
    mutation: spendingMutation(ctx, 'ledger_entries', 'update', id, values),
    undo: undo as ExpensePatch,
  };
}

export function buildDeleteExpense(id: string, ctx: BuildCtx): Mutation {
  return spendingMutation(ctx, 'ledger_entries', 'delete', id);
}

/**
 * v0.1b §14: a restore is an insert for the soft-deleted row's id. The applier clears deletedAt
 * and writes a 'restore' history row. Empty values, so a concurrent edit on another device isn't overwritten.
 */
export function buildRestoreExpense(id: string, ctx: BuildCtx): Mutation {
  return spendingMutation(ctx, 'ledger_entries', 'insert', id, {});
}

export function buildBulkCategorize(
  ids: readonly string[],
  categoryId: string | null,
  ctx: BuildCtx,
): Mutation[] {
  return [...new Set(ids)].map((id) =>
    spendingMutation(ctx, 'ledger_entries', 'update', id, { categoryId }),
  );
}

/**
 * Entries made by another module keep their amount and date in sync with that module. Category,
 * note, (v0.2) the fund and (v0.5) the account can always change. v0.7.6: a Shopping trip's or
 * Quit Smoking day's expense can also move to a card (its method), since neither module writes
 * the method after the insert; Payments rows keep theirs (a card charge or a payment).
 */
export function editableFields(e: { sourceModule: string }): ReadonlyArray<keyof ExpenseFields> {
  if (e.sourceModule === 'spending') return ALL_FIELDS;
  if (e.sourceModule === 'shopping' || e.sourceModule === 'quit_smoking')
    return ['categoryId', 'note', 'method', 'fundId', 'accountId'];
  return ['categoryId', 'note', 'fundId', 'accountId'];
}

/**
 * The expense.updated event for an edit (v0.2), or null when the edit changed nothing.
 * One event per edit carries every changed field, so a cascade emits at most one per entity.
 */
export function expenseUpdatedEvent(
  id: string,
  sourceModule: string,
  before: ExpensePatch,
  after: ExpensePatch,
  mutation: Mutation | null,
  ctx: BuildCtx,
): DomainEvent | null {
  if (!mutation || Object.keys(after).length === 0) return null;
  return {
    type: 'expense.updated',
    expenseId: id,
    before: { ...before },
    after: { ...after },
    sourceModule,
    at: ctx.now,
    mutationId: mutation.id,
  };
}

/** Spending's own entries, and income Budget owns with no payday behind it (isOtherIncome). */
export function canDelete(e: {
  sourceModule: string;
  kind?: string;
  sourceId?: string | null;
}): boolean {
  return e.sourceModule === 'spending' || isOtherIncome(e);
}
